/**
 * Themeable component prop declarations and enums.
 */
import { CSSObject, CSSProp, ThemedStyledProps } from "../styled"
import { whiteSpace } from "../whiteSpace"
import * as Icon from "./Icon"

import Alert from "./Alert"
import Button from "./Button"
import Checkbox from "./Checkbox"
import CheckboxSet from "./CheckboxSet"
import Dialog from "./Dialog"
import Field from "./Field"
import Fieldset from "./Fieldset"
import Input from "./Input"
import Legend from "./Legend"
import Link from "./Link"
import Menu from "./Menu"
import Popover from "./Popover"
import RadioSet from "./RadioSet"
import Select from "./Select"
import Separator from "./Separator"
import Spinner from "./Spinner"
import Switch from "./Switch"
import Tab from "./Tab"
import Table from "./Table"
import TableHeadCell from "./TableHeadCell"
import TableRow from "./TableRow"
import TableSection from "./TableSection"
import ToastNotification from "./ToastNotification"
import ToggleButton from "./ToggleButton"
import Toolbar from "./Toolbar"
import Tooltip from "./Tooltip"
import Text from "./Text"

/**
 * One off typings for the Icon component since the SVG's are housed in the
 * Design Tokens.
 */
export type Icon = {
  Name: Icon.Icon
  Size: Icon.Size
}
export type Sizing = { readonly [key in Icon.Size]: number }
export type Icons = { readonly [key in Icon.Icon]: React.ComponentType<any> }

type EnumerableProps = Omit<typeof enumerableProps, "Icon">
type Property<T, K> = K extends keyof T ? T[K] : never
type AppearanceOf<K> = {
  appearance: Property<Property<EnumerableProps, K>, "appearances">[number]
}
type Props<T extends string | number | symbol> = ThemedStyledProps<{
  appearance: T
}>
type ThemeProps<T extends string | number | symbol> = {
  appearances: Record<T, CSSObject | CSSProp>
}
type ComponentKeys = Exclude<
  keyof EnumerableProps,
  | "Dialog"
  | "Field"
  | "Menu"
  | "Popover"
  | "RadioSet"
  | "Spinner"
  | "Select"
  | "Tab"
  | "TableRow"
>
type AppearanceProps<T extends string | number | symbol> = {
  [K in T]: AppearanceOf<K>
}

/**
 * Intermediate types for the Design Token style implementation and the
 * appearance interpolations.
 */
type ComponentProps = AppearanceProps<ComponentKeys> & {
  Dialog: AppearanceOf<"Dialog"> & {
    Disclosure: {
      appearance: typeof enumerableProps.Dialog.Disclosure.appearances[number]
    }
  }
  Field: AppearanceOf<"Field"> & {
    Label: {
      appearance: typeof enumerableProps.Field.Label.appearances[number]
    }
    Messages: {
      appearance: typeof enumerableProps.Field.Messages.appearances[number]
    }
    Text: {
      appearance: typeof enumerableProps.Field.Text.appearances[number]
    }
  }
  Menu: AppearanceOf<"Menu"> & {
    Disclosure: {
      appearance: typeof enumerableProps.Menu.Disclosure.appearances[number]
    }
    Separator: {
      appearance: typeof enumerableProps.Menu.Separator.appearances[number]
    }
    Group: {
      appearance: typeof enumerableProps.Menu.Group.appearances[number]
    }
    Item: {
      appearance: typeof enumerableProps.Menu.Item.appearances[number]
    }
    Title: {
      appearance: typeof enumerableProps.Menu.Title.appearances[number]
    }
  }
  Popover: AppearanceOf<"Popover"> & {
    Disclosure: {
      appearance: typeof enumerableProps.Popover.Disclosure.appearances[number]
    }
  }
  RadioSet: AppearanceOf<"RadioSet"> & {
    Option: {
      appearance: typeof enumerableProps.RadioSet.Option.appearances[number]
    }
  }
  Select: AppearanceOf<"Select"> & {
    subcomponent: typeof enumerableProps.Select.subcomponents[number]
  }
  Spinner: AppearanceOf<"Spinner"> & {
    subcomponent: typeof enumerableProps.Spinner.subcomponents[number]
  }
  Tab: AppearanceOf<"Tab"> & {
    Panel: {
      appearance: typeof enumerableProps.Tab.Panel.appearances[number]
    }
  }
  TableRow: AppearanceOf<"TableRow"> & {
    stateAppearance: typeof enumerableProps.TableRow.stateAppearances[number]
  }
}

/**
 * Typings for the Design Token libraries to implement against and for the
 * component appearance prop in Core.
 */
export type Components = {
  Alert: ThemeProps<ComponentProps["Alert"]["appearance"]> & {
    Icon: ThemeProps<ComponentProps["Alert"]["appearance"]>
  }
  Button: ThemeProps<ComponentProps["Button"]["appearance"]>
  Checkbox: ThemeProps<ComponentProps["Checkbox"]["appearance"]>
  CheckboxSet: ThemeProps<ComponentProps["CheckboxSet"]["appearance"]>
  Dialog: ThemeProps<ComponentProps["Dialog"]["appearance"]> & {
    Backdrop: ThemeProps<ComponentProps["Dialog"]["appearance"]>
    Disclosure: ThemeProps<ComponentProps["Dialog"]["Disclosure"]["appearance"]>
  }
  Separator: ThemeProps<ComponentProps["Separator"]["appearance"]>
  Field: ThemeProps<ComponentProps["Field"]["appearance"]> & {
    Label: ThemeProps<ComponentProps["Field"]["Label"]["appearance"]>
    Messages: ThemeProps<ComponentProps["Field"]["Messages"]["appearance"]>
    Text: ThemeProps<ComponentProps["Field"]["Text"]["appearance"]>
  }
  Fieldset: ThemeProps<ComponentProps["Fieldset"]["appearance"]>
  Icon: { sizing: Sizing }
  Input: ThemeProps<ComponentProps["Input"]["appearance"]>
  Legend: ThemeProps<ComponentProps["Legend"]["appearance"]>
  Link: ThemeProps<ComponentProps["Link"]["appearance"]>
  Menu: ThemeProps<ComponentProps["Menu"]["appearance"]> & {
    Disclosure: ThemeProps<ComponentProps["Menu"]["Disclosure"]["appearance"]>
    Separator: ThemeProps<ComponentProps["Menu"]["Separator"]["appearance"]>
    Group: ThemeProps<ComponentProps["Menu"]["Group"]["appearance"]>
    Item: ThemeProps<ComponentProps["Menu"]["Item"]["appearance"]>
    Title: ThemeProps<ComponentProps["Menu"]["Title"]["appearance"]>
  }
  Popover: ThemeProps<ComponentProps["Popover"]["appearance"]> & {
    Disclosure: ThemeProps<
      ComponentProps["Popover"]["Disclosure"]["appearance"]
    >
  }
  RadioSet: ThemeProps<ComponentProps["RadioSet"]["appearance"]> & {
    Option: ThemeProps<ComponentProps["RadioSet"]["Option"]["appearance"]>
  }
  Tab: ThemeProps<ComponentProps["Tab"]["appearance"]> & {
    Panel: ThemeProps<ComponentProps["Tab"]["Panel"]["appearance"]>
  }
  Table: ThemeProps<ComponentProps["Table"]["appearance"]>
  TableHeadCell: ThemeProps<ComponentProps["TableHeadCell"]["appearance"]>
  TableRow: ThemeProps<
    | ComponentProps["TableRow"]["appearance"]
    | ComponentProps["TableRow"]["stateAppearance"]
  >
  TableSection: ThemeProps<ComponentProps["TableSection"]["appearance"]>
  ToggleButton: ThemeProps<ComponentProps["ToggleButton"]["appearance"]>
  Toolbar: ThemeProps<ComponentProps["Toolbar"]["appearance"]>
  Tooltip: ThemeProps<ComponentProps["Tooltip"]["appearance"]>
  Select: ThemeProps<ComponentProps["Select"]["appearance"]> & {
    subcomponents: Record<
      ComponentProps["Select"]["subcomponent"],
      CSSObject | CSSProp
    >
  }
  Spinner: ThemeProps<ComponentProps["Spinner"]["appearance"]> & {
    subcomponents: Record<
      ComponentProps["Spinner"]["subcomponent"],
      CSSObject | CSSProp
    >
  }
  Text: ThemeProps<ComponentProps["Text"]["appearance"]>
  Switch: ThemeProps<ComponentProps["Switch"]["appearance"]> & {
    Toggle: ThemeProps<ComponentProps["Switch"]["appearance"]>
  }
  ToastNotification: ThemeProps<
    ComponentProps["ToastNotification"]["appearance"]
  >
}

/**
 * The enumerable appearance prop values that are exported for use in testing
 * with Storybook.
 */
const enumerableProps = {
  Alert,
  Button,
  Checkbox,
  CheckboxSet,
  Dialog,
  Field,
  Fieldset,
  Icon: Icon.enumerableProps,
  Input,
  Legend,
  Link,
  Menu,
  Popover,
  RadioSet,
  Select,
  Separator,
  Spinner,
  Switch,
  Tab,
  Table,
  TableHeadCell,
  TableRow,
  TableSection,
  Text,
  ToastNotification,
  ToggleButton,
  Toolbar,
  Tooltip
}

/**
 * These are function (props => styles) for extracting the appearace styling
 * from the Design Tokens.
 */
const interpolations = {
  Alert: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Alert"]["appearance"]>) =>
      theme.components.Alert.appearances[appearance],
    Icon: {
      appearance: ({
        appearance,
        theme
      }: Props<ComponentProps["Alert"]["appearance"]>) =>
        theme.components.Alert.Icon.appearances[appearance]
    }
  },
  Button: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Button"]["appearance"]>) =>
      theme.components.Button.appearances[appearance]
  },
  Checkbox: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Checkbox"]["appearance"]>) =>
      theme.components.Checkbox.appearances[appearance]
  },
  CheckboxSet: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["CheckboxSet"]["appearance"]>) =>
      theme.components.CheckboxSet.appearances[appearance]
  },
  Dialog: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Dialog"]["appearance"]>) =>
      theme.components.Dialog.appearances[appearance],
    Backdrop: {
      appearance: ({
        appearance,
        theme
      }: Props<ComponentProps["Dialog"]["appearance"]>) =>
        theme.components.Dialog.Backdrop.appearances[appearance]
    },
    Disclosure: {
      appearance: ({
        appearance,
        theme
      }: Props<ComponentProps["Dialog"]["Disclosure"]["appearance"]>) =>
        theme.components.Dialog.Disclosure.appearances[appearance]
    }
  },
  Field: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Field"]["appearance"]>) =>
      theme.components.Field.appearances[appearance],
    Label: {
      appearance: ({
        appearance,
        theme
      }: Props<ComponentProps["Field"]["Label"]["appearance"]>) =>
        theme.components.Field.Label.appearances[appearance]
    },
    Messages: {
      appearance: ({
        appearance,
        theme
      }: Props<ComponentProps["Field"]["Messages"]["appearance"]>) =>
        theme.components.Field.Messages.appearances[appearance]
    },
    Text: {
      appearance: ({
        appearance,
        theme
      }: Props<ComponentProps["Field"]["Text"]["appearance"]>) =>
        theme.components.Field.Text.appearances[appearance]
    }
  },
  Fieldset: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Fieldset"]["appearance"]>) =>
      theme.components.Fieldset.appearances[appearance]
  },
  Input: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Input"]["appearance"]>) =>
      theme.components.Input.appearances[appearance]
  },
  Legend: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Legend"]["appearance"]>) =>
      theme.components.Legend.appearances[appearance]
  },
  Link: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Link"]["appearance"]>) =>
      theme.components.Link.appearances[appearance]
  },
  Menu: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Menu"]["appearance"]>) =>
      theme.components.Menu.appearances[appearance],
    Disclosure: {
      appearance: ({
        appearance,
        theme
      }: Props<ComponentProps["Menu"]["Disclosure"]["appearance"]>) =>
        theme.components.Menu.Disclosure.appearances[appearance]
    },
    Separator: {
      appearance: ({
        appearance,
        theme
      }: Props<ComponentProps["Menu"]["Separator"]["appearance"]>) =>
        theme.components.Menu.Separator.appearances[appearance]
    },
    Group: {
      appearance: ({
        appearance,
        theme
      }: Props<ComponentProps["Menu"]["Group"]["appearance"]>) =>
        theme.components.Menu.Group.appearances[appearance]
    },
    Item: {
      appearance: ({
        appearance,
        theme
      }: Props<ComponentProps["Menu"]["Item"]["appearance"]>) =>
        theme.components.Menu.Item.appearances[appearance]
    },
    Title: {
      appearance: ({
        appearance,
        theme
      }: Props<ComponentProps["Menu"]["Title"]["appearance"]>) =>
        theme.components.Menu.Title.appearances[appearance]
    }
  },
  Popover: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Popover"]["appearance"]>) =>
      theme.components.Popover.appearances[appearance],
    Disclosure: {
      appearance: ({
        appearance,
        theme
      }: Props<ComponentProps["Popover"]["Disclosure"]["appearance"]>) =>
        theme.components.Popover.Disclosure.appearances[appearance]
    }
  },
  RadioSet: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["RadioSet"]["appearance"]>) =>
      theme.components.RadioSet.appearances[appearance],
    Option: {
      appearance: ({
        appearance,
        theme
      }: Props<ComponentProps["RadioSet"]["Option"]["appearance"]>) =>
        theme.components.RadioSet.Option.appearances[appearance]
    }
  },
  Separator: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Separator"]["appearance"]>) =>
      theme.components.Separator.appearances[appearance]
  },
  Select: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Select"]["appearance"]>) =>
      theme.components.Select.appearances[appearance],
    subcomponents: ({
      subcomponent,
      theme
    }: ThemedStyledProps<{
      subcomponent: typeof enumerableProps.Select.subcomponents[number]
    }>) => {
      return subcomponent && !!theme?.components
        ? theme.components.Select.subcomponents[subcomponent!]
        : ""
    }
  },
  Spinner: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Spinner"]["appearance"]>) =>
      theme.components.Spinner.appearances[appearance],
    subcomponent: ({
      subcomponent,
      theme
    }: ThemedStyledProps<{
      subcomponent: typeof enumerableProps.Spinner.subcomponents[number]
    }>) => {
      return subcomponent && !!theme?.components
        ? theme.components.Spinner.subcomponents[subcomponent]
        : ""
    }
  },
  Switch: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Switch"]["appearance"]>) =>
      theme.components.Switch.appearances[appearance],
    Toggle: {
      appearance: ({
        appearance,
        theme
      }: Props<ComponentProps["Switch"]["appearance"]>) =>
        theme.components.Switch.Toggle.appearances[appearance]
    }
  },
  Tab: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Tab"]["appearance"]>) =>
      theme.components.Tab.appearances[appearance],
    Panel: {
      appearance: ({
        appearance,
        theme
      }: Props<ComponentProps["Tab"]["Panel"]["appearance"]>) =>
        theme.components.Tab.Panel.appearances[appearance]
    }
  },
  Table: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Table"]["appearance"]>) =>
      theme.components.Table.appearances[appearance]
  },
  TableHeadCell: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["TableHeadCell"]["appearance"]>) =>
      theme.components.TableHeadCell.appearances[appearance]
  },
  TableRow: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["TableRow"]["appearance"]>) =>
      theme.components.TableRow.appearances[appearance],
    stateAppearances: ({
      state,
      theme
    }: ThemedStyledProps<{
      state: typeof enumerableProps.TableRow.stateAppearances[number]
    }>) => theme.components.TableRow.appearances[state]
  },
  TableSection: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["TableSection"]["appearance"]>) =>
      theme.components.TableSection.appearances[appearance]
  },
  ToastNotification: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["ToastNotification"]["appearance"]>) =>
      theme.components.ToastNotification.appearances[appearance]
  },
  ToggleButton: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["ToggleButton"]["appearance"]>) =>
      theme.components.ToggleButton.appearances[appearance]
  },
  Toolbar: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Toolbar"]["appearance"]>) =>
      theme.components.Toolbar.appearances[appearance]
  },
  Tooltip: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Tooltip"]["appearance"]>) =>
      theme.components.Tooltip.appearances[appearance]
  },
  Text: {
    appearance: ({
      appearance,
      theme
    }: Props<ComponentProps["Text"]["appearance"]>) =>
      theme.components.Text.appearances[appearance],
    // FIXME: This file should only contain appearance interpolations. Color should be moved into the component.
    color: ({ color }: { color: string }) => ({ color }),
    whiteSpace
  }
}

export { enumerableProps, interpolations }
